import React, { useState } from 'react';
import { MultipleStepCustomisation } from 'src/components/BundleProduct/MultipleStepCustomisation/MultipleStepCustomisation';
import { EMPTY_ARTWORK_ID } from 'src/constants/artwork';
import { getCustomisationCost } from 'src/components/BundleProduct/utils/getCustomisationCost';
import { organizeArtworkPriceInfo } from 'src/components/BundleProduct/ProductBundleSummary/helpers';
import { convertCustomisationToArtworkData } from 'src/components/BundleProduct/utils/convertCustomisationToArtworkData';
import { MixAndMatchChosenCustomisation } from 'src/MixAndMatchBundle/MixAndMatchChosenCustomisation';
import { camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_locations } from 'src/queries/__generated__/wearerBundleDetail';
import snakecaseKeys from 'snakecase-keys';
import { ProductLocation } from 'src/components/CustomisationPage/feature/customisation.types';
import { Customisation } from 'src/components/BundleProduct/types';
import { CircleLoader } from 'src/components/CircleLoader/CircleLoader';

import classes from './MixAndMatchBundleCustomisation.scss';

type Props = {
    setFormikValueOfCustomisations: (customisations: Customisation[]) => void;
    formikCustomisations: Customisation[];
    maxQty: number;
    isValid: boolean;
    locations: camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_locations[];
    onAddToCartClick: () => void;
    basketLoading: boolean;
    stepNr: number;
};

export const MixAndMatchBundleCustomisation: React.FC<Props> = ({
    setFormikValueOfCustomisations,
    formikCustomisations,
    maxQty,
    isValid,
    locations,
    onAddToCartClick,
    basketLoading,
    stepNr,
}) => {
    const [openCustomisationModal, setOpenCustomisationModal] = useState(false);

    const [firstCustomisation] = formikCustomisations;

    const artworksData = convertCustomisationToArtworkData(formikCustomisations);
    const { artworkCost } = organizeArtworkPriceInfo({
        artworkPriceBreak: undefined,
        artworksData,
        maxQty: maxQty,
    });

    const handleCreateCustomisation = (customisation: Customisation) => {
        const customisations = formikCustomisations;
        customisations.push(customisation);

        setFormikValueOfCustomisations(customisations);
    };

    const handleChangeCustomisation = (customisation: Customisation, index: number) => {
        const customisations = formikCustomisations;
        if (customisations[index].selectedMethod !== customisation.selectedMethod) {
            customisations.forEach(
                (valueCustomisation) => (valueCustomisation.selectedMethod = customisation.selectedMethod),
            );
        }
        customisations[index] = customisation;

        setFormikValueOfCustomisations(customisations);
    };

    const handleDeleteCustomisation = (index: number) => {
        const customisations = formikCustomisations.filter((c, i) => i !== index);

        setFormikValueOfCustomisations(customisations);
    };

    const isCustomisationAdded = formikCustomisations.length !== 0;

    return (
        <div id="container-add-a-logo" className={classes.stepAndProductContainer}>
            <h3 className={classes.title}>
                {`Step ${stepNr} - `}
                <div className={classes.headerContainer}>
                    <span className={classes.header}>Add a logo</span>
                    <span className={classes.headerSubtitle}>it's free!</span>
                </div>
            </h3>
            <div className={classes.container}>
                <div className={classes.textAndLogoContainer}>
                    <div className={classes.textContainer}>
                        <p className={classes.description}>
                            Choose between embroidery or printed text. Create or upload your design and we’ll do the
                            rest!
                        </p>
                    </div>
                    <div className={classes.addLogoContainer}>
                        <button
                            type="button"
                            className={classes.addLogoButton}
                            onClick={() => {
                                if (isCustomisationAdded) {
                                    onAddToCartClick();
                                } else {
                                    setOpenCustomisationModal(true);
                                }
                            }}
                            disabled={!isValid}
                        >
                            {isCustomisationAdded ? (
                                basketLoading ? (
                                    <div className={classes.loaderWrapper}>
                                        <CircleLoader />
                                    </div>
                                ) : (
                                    'Add to basket'
                                )
                            ) : (
                                'Add a logo'
                            )}
                        </button>
                    </div>
                </div>

                {formikCustomisations.length > 0 && (
                    <div className={classes.chosenCustomisationsContainer}>
                        <div className={classes.chosenCustomisations}>
                            {formikCustomisations.map((customisation, index) => (
                                <MixAndMatchChosenCustomisation
                                    key={index}
                                    locations={locations}
                                    customisation={customisation}
                                    onEdit={(customisations) => handleChangeCustomisation(customisations, index)}
                                    onDelete={handleDeleteCustomisation}
                                    index={index}
                                    customisationCost={getCustomisationCost({
                                        customisation,
                                        artworkCosts: artworkCost,
                                        artworkPriceBreak: undefined,
                                        maxQty: maxQty,
                                        index,
                                    })}
                                    alreadySelectedPositions={
                                        index > 0 ? firstCustomisation?.selectedPositions : undefined
                                    }
                                />
                            ))}
                        </div>
                    </div>
                )}

                {openCustomisationModal && (
                    <MultipleStepCustomisation
                        locations={snakecaseKeys(locations as any) as unknown as ProductLocation[]}
                        isOpenModal
                        closeModal={() => setOpenCustomisationModal(false)}
                        onSubmitCustomisation={(values) => handleCreateCustomisation(values)}
                        customisation={
                            firstCustomisation?.selectedMethod
                                ? {
                                      selectedMethod: firstCustomisation?.selectedMethod,
                                      selectedArtwork: EMPTY_ARTWORK_ID,
                                      selectedPositions: [
                                          locations.find((location) =>
                                              firstCustomisation?.selectedPositions?.includes(location.name),
                                          )?.name || '',
                                      ],
                                  }
                                : undefined
                        }
                        alreadySelectedPositions={firstCustomisation?.selectedPositions}
                        freeCustomisation={formikCustomisations.length === 0}
                        avoidSelectingPosition={formikCustomisations.length !== 0}
                        recommendedApplication={undefined}
                    />
                )}
            </div>
        </div>
    );
};
